import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import {
  atLeastOneCheckedValidator,
  lessThanOrEqualToValidator,
} from 'src/app/shared/validators/registration-validators';
import { equalDayMonthYear, monthsAgo, yearsAgo } from 'src/app/utils/date';
import { download } from 'src/app/utils/file';

export interface CreateReportModalData {
  startingDate?: Date;
  endingDate?: Date;
  includeAddedPoint?: boolean;
  includeRedeemedPoints?: boolean;
  includeExpiredPoints?: boolean;
  familyId?: number;
}

@Component({
  selector: 'app-modal',
  templateUrl: './create-report-modal.component.html',
  styleUrls: ['./create-report-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Modal molecule
 */
export class CreateReportModalComponent implements OnInit, OnDestroy {
  form = new FormGroup(
    {
      startingDate: new FormControl<Date | null>(null),
      endingDate: new FormControl<Date | null>(null),
      includeAddedPoints: new FormControl<boolean>(true),
      includeRedeemedPoints: new FormControl<boolean>(true),
      includeExpiredPoints: new FormControl<boolean>(true),
      includeRemovedPoints: new FormControl<boolean>(true),
    },
    {
      validators: atLeastOneCheckedValidator([
        'includeAddedPoints',
        'includeRedeemedPoints',
        'includeExpiredPoints',
        'includeRemovedPoints',
      ]),
    }
  );
  private subsink = new Subscription();
  labels = {
    date: 'date',
    last3Months: 'last-months',
    last6Months: 'last-months',
    lastYear: 'last-year',
    dateRange: 'specific-date-range',
    include: 'include',
    expiredPoints: 'expired-points-during-period',
    addedPoints: 'added-points-during-period',
    redeemedPoints: 'redeemed-points-during-period',
    removedPoints: 'removed-points-during-period',
    atLeastOneFilterRequired: 'select-at-least-one-filter',
    startingDate: 'starting-date',
    endingDate: 'ending-date',
    createReport: 'create-report',
    createAReport: 'create-a-report',
    cancel: 'cancel',
    errorCreatingReport: 'error-creating-report',
    ok: 'ok',
    successCreatingReport: 'success-creating-report',
  };
  activeFilter = {
    last3Months: false,
    last6Months: false,
    lastYear: false,
  };
  loading = false;

  /**
   * @constructor
   */
  constructor(
    private dialogRef: MatDialogRef<CreateReportModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateReportModalData,
    private readonly translationService: TranslationService,
    private readonly rewardService: RewardService,
    private readonly snackbarService: SnackBarService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    const endingDateControl = this.form.get('endingDate')!;
    const startingDateControl = this.form.get('startingDate')!;

    startingDateControl.addValidators(
      lessThanOrEqualToValidator(endingDateControl, 'invalid-starting-date')
    );

    this.subsink.add(
      endingDateControl.valueChanges.subscribe(() => {
        startingDateControl.updateValueAndValidity();
      })
    );

    this.subsink.add(
      startingDateControl.valueChanges.subscribe((value) => {
        if (value != null) {
          this.activateAppropriateFilter();
        } else if (endingDateControl.value == null) {
          this.last3Months();
        }
      })
    );
    this.subsink.add(
      endingDateControl.valueChanges.subscribe((value) => {
        if (value != null) {
          this.activateAppropriateFilter();
        } else if (startingDateControl.value == null) {
          this.last3Months();
        }
      })
    );

    Promise.all([
      this.translationService.translatePlural(this.labels.last6Months, 6),
      this.translationService.translatePlural(this.labels.last3Months, 3),
    ]).then(([s6, s3]) => {
      this.labels.last6Months = s6;
      this.labels.last3Months = s3;
      this.translationService.translateObject(this.labels);
    });

    this.last3Months();
  }

  /**
   * clean up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Action to create and download report
   */
  createReport() {
    this.loading = true;
    const startDate = this.form.get('startingDate')!.value || undefined;
    const endDate = this.form.get('endingDate')!.value || undefined;
    const includeAdded = this.form.get('includeAddedPoints')!.value || false;
    const includeExpired =
      this.form.get('includeExpiredPoints')!.value || false;
    const includeRedeemed =
      this.form.get('includeRedeemedPoints')!.value || false;
    const includeRemoved =
      this.form.get('includeRemovedPoints')!.value || false;
    const familyId = this.data.familyId!;

    this.subsink.add(
      this.rewardService
        .createReport({
          includeAdded,
          includeExpired,
          includeRedeemed,
          includeRemoved,
          startDate,
          endDate,
          familyId,
        })
        .subscribe({
          next: (report) => {
            download('report.csv', report, 'csv');
            this.snackbarService.success(this.labels.successCreatingReport);
            this.dialogRef.close();
          },
          error: () => {
            this.snackbarService.error(this.labels.errorCreatingReport);
          },
        })
        .add(() => {
          this.loading = false;
        })
    );
  }

  /**
   * Action to close the dialog
   */
  onFailClick() {
    this.dialogRef.close(MatDialogResponse.FAIL);
  }

  /**
   * Sets date filters to three months ago
   */
  last3Months() {
    this.lastMonths(3);
    this.unsetFilters();
    this.activeFilter.last3Months = true;
  }

  /**
   * Sets date filters to six months ago
   */
  last6Months() {
    this.lastMonths(6);
    this.unsetFilters();
    this.activeFilter.last6Months = true;
  }

  /**
   * Sets date filter form control as some months ago
   * @param {number} months number must be from 1 to 11, inclusive.
   */
  lastMonths(months: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11) {
    const date = monthsAgo(months);
    this.form.get('startingDate')!.setValue(date);
    this.form.get('endingDate')!.setValue(new Date());
  }

  /**
   * Sets date filter form control as one year ago
   */
  lastYear() {
    const date = yearsAgo(1);
    this.form.get('startingDate')!.setValue(date);
    this.form.get('endingDate')!.setValue(new Date());
    this.unsetFilters();
    this.activeFilter.lastYear = true;
  }

  /**
   * unsets all date filters
   */
  private unsetFilters() {
    this.activeFilter.last3Months = false;
    this.activeFilter.last6Months = false;
    this.activeFilter.lastYear = false;
  }

  /**
   * activates the proper filter depending on starting and ending date
   */
  private activateAppropriateFilter() {
    const startingDate = this.form.controls['startingDate'].value;
    const endingDate = this.form.controls['endingDate'].value || new Date();
    if (!startingDate || !equalDayMonthYear(endingDate, new Date())) {
      return;
    }
    this.unsetFilters();
    if (equalDayMonthYear(startingDate, monthsAgo(3))) {
      this.activeFilter.last3Months = true;
    } else if (equalDayMonthYear(startingDate, monthsAgo(6))) {
      this.activeFilter.last6Months = true;
    } else if (equalDayMonthYear(startingDate, yearsAgo(1))) {
      this.activeFilter.lastYear = true;
    }
  }
}
